import React, { useState } from 'react';
import { useWizard } from '../../context/WizardContext';
import { ArrowLeft, ArrowRight, MessageSquare, BarChart3, GitMerge, Info, CheckCircle } from 'lucide-react';

const B1_AnalysisApproach = () => {
  const { dataA, dataB, updateDataRaw, nextStep, prevStep, logDecision } = useWizard();
  const [selectedApproach, setSelectedApproach] = useState(dataB?.analysisApproach || '');

  const methodologyFromA = dataA?.methodologicalChoice;

  const approaches = [
    {
      id: 'qualitative',
      icon: MessageSquare,
      title: 'Qualitative Only',
      color: '#7c3aed',
      bgColor: '#f5f3ff',
      borderColor: '#c4b5fd',
      description: 'Analyze text, themes, and meanings from open-ended responses',
      examples: ['Thematic Analysis', 'Content Analysis', 'Sentiment Analysis', 'Narrative Analysis'],
      bestFor: 'Understanding experiences, exploring phenomena, generating theory'
    },
    {
      id: 'quantitative',
      icon: BarChart3,
      title: 'Quantitative Only',
      color: '#0891b2',
      bgColor: '#ecfeff',
      borderColor: '#67e8f9',
      description: 'Analyze numerical data using statistics and tests',
      examples: ['Descriptive Statistics', 'T-tests', 'ANOVA', 'Correlation', 'Regression'],
      bestFor: 'Testing hypotheses, measuring relationships, generalizing findings'
    },
    {
      id: 'mixed',
      icon: GitMerge,
      title: 'Mixed Methods',
      color: '#059669',
      bgColor: '#ecfdf5',
      borderColor: '#6ee7b7',
      description: 'Combine both qualitative and quantitative analysis',
      examples: ['All methods above', 'Triangulation', 'Sequential designs'],
      bestFor: 'Comprehensive understanding, validating findings, complex research questions'
    }
  ];

  const getRecommendation = () => {
    if (!methodologyFromA) return null;
    
    const lower = methodologyFromA.toLowerCase();
    if (lower.includes('qualitative') || lower === 'mono-qual') {
      return 'qualitative';
    } else if (lower.includes('quantitative') || lower === 'mono-quant') {
      return 'quantitative';
    } else if (lower.includes('mixed') || lower.includes('multi')) {
      return 'mixed';
    }
    return null;
  };

  const recommendation = getRecommendation();

  const handleSelect = (approachId) => {
    setSelectedApproach(approachId);
  };

  const handleNext = () => {
    updateDataRaw({
      'wizardB.analysisApproach': selectedApproach
    });

    const approachLabel = approaches.find(a => a.id === selectedApproach)?.title || selectedApproach;
    logDecision(
      'Analysis Approach',
      approachLabel,
      `Selected ${approachLabel} for data analysis`
    );

    nextStep();
  };

  return (
    <div className="wizard-step">
      <h2>Step 2: Choose Your Approach</h2>
      <p className="step-description">
        What type of data analysis do you plan to conduct? This determines which methods you can use.
      </p>

      {recommendation && (
        <div style={{
          background: recommendation === 'qualitative' ? '#f5f3ff' : 
                     recommendation === 'quantitative' ? '#ecfeff' : '#ecfdf5',
          border: `1px solid ${recommendation === 'qualitative' ? '#c4b5fd' : 
                              recommendation === 'quantitative' ? '#67e8f9' : '#6ee7b7'}`,
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem'
        }}>
          <Info size={20} style={{ 
            flexShrink: 0, 
            marginTop: '2px',
            color: recommendation === 'qualitative' ? '#7c3aed' : 
                   recommendation === 'quantitative' ? '#0891b2' : '#059669'
          }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: '#1e293b' }}>
              Based on Wizard A: {methodologyFromA}
            </div>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569' }}>
              Your methodology choice suggests <strong>{approaches.find(a => a.id === recommendation)?.title}</strong> analysis 
              would be most appropriate. However, you can select any approach based on your specific needs.
            </p>
          </div>
        </div>
      )}

      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {approaches.map((approach) => {
          const Icon = approach.icon;
          const isSelected = selectedApproach === approach.id;
          const isRecommended = recommendation === approach.id;

          return (
            <div
              key={approach.id}
              onClick={() => handleSelect(approach.id)}
              style={{
                background: isSelected ? approach.bgColor : 'white',
                border: `2px solid ${isSelected ? approach.color : '#e2e8f0'}`,
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
            >
              {isRecommended && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '1rem',
                  background: approach.color,
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  Recommended
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: isSelected ? approach.color : approach.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={24} color={isSelected ? 'white' : approach.color} />
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ margin: 0, color: approach.color }}>{approach.title}</h3>
                    {isSelected && <CheckCircle size={20} color={approach.color} />}
                  </div>
                  
                  <p style={{ margin: '0 0 0.75rem 0', color: '#475569' }}>
                    {approach.description}
                  </p>

                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '0.5rem',
                    marginBottom: '0.75rem'
                  }}>
                    {approach.examples.map((example, idx) => (
                      <span key={idx} style={{
                        background: isSelected ? 'white' : approach.bgColor,
                        color: approach.color,
                        padding: '4px 10px',
                        borderRadius: '16px',
                        fontSize: '0.8rem',
                        fontWeight: 500
                      }}>
                        {example}
                      </span>
                    ))}
                  </div>

                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.85rem', 
                    color: '#64748b',
                    fontStyle: 'italic'
                  }}>
                    Best for: {approach.bestFor}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={!selectedApproach}
        >
          Next Step
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default B1_AnalysisApproach;
